import React, { useState } from 'react';
import { SectionCard } from '@/components/common/SectionCard';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import {
    ShoppingBag, Store, Truck, XCircle,
    Eye, AlertOctagon
} from 'lucide-react';
import { formatRand } from '@/utility/helper';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';

// ─── Types ──────────────────────────────────────────────────────────────────

interface IDashboardOrder {
    id: string; orderId: string; store: string; customer: string;
    amount: number; paymentType: string; status: string;
    driver?: string; isDelayed: boolean; createdAt: string;
}
interface IStoreRow {
    id: string; storeName: string; ordersToday: number; revenue: number;
    avgPrepTime: number; cancellationRate: number; isOnline: boolean; rating: number;
}
interface IDeliveryIssue {
    id: string; orderId: string; driver: string; delayDuration: string;
    issueType: string; escalationStatus: string;
}
interface IFailedOrder {
    id: string; orderId: string; store: string; failureReason: string;
    refundStatus: string; hasComplaint: boolean;
}

interface Props {
    orders: IDashboardOrder[];
    stores: IStoreRow[];
    deliveryIssues: IDeliveryIssue[];
    failedOrders: IFailedOrder[];
    loading?: boolean;
    activeFilter?: string;
}

type Tab = 'orders' | 'stores' | 'delivery' | 'failed';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'orders', label: 'Recent Orders', icon: <ShoppingBag className="w-3.5 h-3.5" /> },
    { key: 'stores', label: 'Store Performance', icon: <Store className="w-3.5 h-3.5" /> },
    { key: 'delivery', label: 'Delivery Issues', icon: <Truck className="w-3.5 h-3.5" /> },
    { key: 'failed', label: 'Failed / Cancelled', icon: <XCircle className="w-3.5 h-3.5" /> },
];

export const BusinessTablesSection: React.FC<Props> = ({
    orders, stores, deliveryIssues, failedOrders, loading = false, activeFilter
}) => {
    const [activeTab, setActiveTab] = useState<Tab>('orders');
    const navigate = useRoleNavigate();

    const getTimeRangeLabel = (filter?: string) => {
        switch (filter) {
            case 'today':
                return 'Today';
            case 'this_week':
                return 'This Week';
            case 'this_month':
                return 'This Month';
            case 'custom':
                return 'Selected Range';
            case '':
                return 'All Time';
            default:
                return 'Today';
        }
    };

    const timeRangeLabel = getTimeRangeLabel(activeFilter);

    // ── Recent Orders Columns ──────────────────────────────────────────────
    const orderColumns: TableColumn<IDashboardOrder>[] = [
        { key: 'orderId', header: 'Order ID', render: (r) => <span className="font-mono text-xs font-semibold">{r.orderId}</span> },
        { key: 'store', header: 'Store', render: (r) => <span className="text-xs">{r.store}</span> },
        { key: 'customer', header: 'Customer', render: (r) => <span className="text-xs">{r.customer}</span> },
        { key: 'amount', header: 'Amount', render: (r) => <span className="text-xs font-semibold">{formatRand(r.amount)}</span> },
        { key: 'paymentType', header: 'Payment', render: (r) => <span className="text-[10px] bg-secondary px-2 py-0.5 rounded font-medium">{r.paymentType}</span> },
        { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
        { key: 'driver', header: 'Driver', render: (r) => <span className="text-xs text-muted-foreground">{r.driver || '—'}</span> },
        {
            key: 'isDelayed', header: 'Delay', render: (r) => r.isDelayed
                ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><AlertOctagon className="w-3 h-3" />Delayed</span>
                : <span className="text-[10px] text-green-600">On Time</span>
        },
        { key: 'createdAt', header: 'Time', render: (r) => <span className="text-xs text-muted-foreground">{r.createdAt}</span> },
    ];

    // ── Store Performance Columns ─────────────────────────────────────────
    const storeColumns: TableColumn<IStoreRow>[] = [
        { key: 'storeName', header: 'Store', render: (r) => <span className="font-semibold text-xs">{r.storeName}</span> },
        { key: 'ordersToday', header: `Orders (${timeRangeLabel})`, render: (r) => <span className="text-xs font-semibold">{r.ordersToday}</span> },
        { key: 'revenue', header: 'Revenue', render: (r) => <span className="text-xs font-semibold text-green-600">{formatRand(r.revenue)}</span> },
        { key: 'avgPrepTime', header: 'Avg Prep', render: (r) => <span className="text-xs">{r.avgPrepTime} min</span> },
        {
            key: 'cancellationRate', header: 'Cancel %', render: (r) => (
                <span className={`text-xs font-semibold ${r.cancellationRate > 10 ? 'text-red-500' : 'text-foreground'}`}>{r.cancellationRate}%</span>
            )
        },
        { key: 'isOnline', header: 'Status', render: (r) => <StatusBadge status={r.isOnline ? 'online' : 'offline'} /> },
        {
            key: 'rating', header: 'Rating', render: (r) => (
                <span className="text-xs font-semibold flex items-center gap-1">⭐ {r.rating.toFixed(1)}</span>
            )
        },
    ];

    // ── Delivery Issues Columns ───────────────────────────────────────────
    const deliveryColumns: TableColumn<IDeliveryIssue>[] = [
        { key: 'orderId', header: 'Order ID', render: (r) => <span className="font-mono text-xs font-semibold">{r.orderId}</span> },
        { key: 'driver', header: 'Driver', render: (r) => <span className="text-xs">{r.driver}</span> },
        { key: 'delayDuration', header: 'Delay', render: (r) => <span className="text-xs text-red-500 font-semibold">{r.delayDuration}</span> },
        { key: 'issueType', header: 'Issue', render: (r) => <span className="text-xs">{r.issueType}</span> },
        {
            key: 'escalationStatus', header: 'Escalation', render: (r) => (
                <StatusBadge status={r.escalationStatus === 'Resolved' ? 'completed' : r.escalationStatus === 'Escalated' ? 'warning' : 'pending'} />
            )
        },
    ];

    // ── Failed Orders Columns ─────────────────────────────────────────────
    const failedColumns: TableColumn<IFailedOrder>[] = [
        { key: 'orderId', header: 'Order ID', render: (r) => <span className="font-mono text-xs font-semibold">{r.orderId}</span> },
        { key: 'store', header: 'Store', render: (r) => <span className="text-xs">{r.store}</span> },
        { key: 'failureReason', header: 'Reason', render: (r) => <span className="text-xs text-red-500">{r.failureReason}</span> },
        {
            key: 'refundStatus', header: 'Refund', render: (r) => (
                <StatusBadge status={r.refundStatus === 'Processed' ? 'completed' : r.refundStatus === 'Pending' ? 'pending' : 'inactive'} />
            )
        },
        {
            key: 'hasComplaint', header: 'Complaint', render: (r) => r.hasComplaint
                ? <span className="text-[10px] text-red-600 font-semibold">Yes</span>
                : <span className="text-[10px] text-muted-foreground">No</span>
        },
    ];

    return (
        <SectionCard
            title="Live Operations"
            icon={<ShoppingBag />}
            extra={
                <div className="flex gap-1">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                                ${activeTab === tab.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                        >
                            {tab.icon}{tab.label}
                        </button>
                    ))}
                </div>
            }
        >
            <div className="pt-2">
                {activeTab === 'orders' && (
                    <CommonTable
                        columns={orderColumns}
                        data={orders}
                        loading={loading}
                        onRowClick={(r) => navigate(RoutesLink.ViewOrders.replace(':id', r.id))}
                        actions={(row) => [
                            { text: 'View Order', icon: <Eye className="w-4 h-4 text-primary" />, onClick: (r) => navigate(RoutesLink.ViewOrders.replace(':id', r.id)) },
                        ]}
                        emptyMessage="No recent orders"
                    />
                )}
                {activeTab === 'stores' && (
                    <CommonTable
                        columns={storeColumns}
                        data={stores}
                        loading={loading}
                        onRowClick={(row) => navigate(RoutesLink.storeDetails.replace(':id', row.id))}
                        actions={(row) => [
                            { text: 'View Details', icon: <Eye className="w-4 h-4 text-primary" />, onClick: (r) => navigate(RoutesLink.storeDetails.replace(':id', r.id)) },
                        ]}
                        emptyMessage="No store data"
                    />
                )}
                {activeTab === 'delivery' && (
                    <CommonTable
                        columns={deliveryColumns}
                        data={deliveryIssues}
                        loading={loading}
                        onRowClick={(row) => navigate(RoutesLink.ViewOrders.replace(':id', row.id))}
                        actions={(row) => [
                            { text: 'View Order', icon: <Eye className="w-4 h-4 text-primary" />, onClick: (r) => navigate(RoutesLink.ViewOrders.replace(':id', r.id)) },
                        ]}
                        emptyMessage="No delivery issues"
                    />
                )}
                {activeTab === 'failed' && (
                    <CommonTable
                        columns={failedColumns}
                        data={failedOrders}
                        loading={loading}
                        onRowClick={(row) => navigate(RoutesLink.ViewOrders.replace(':id', row.id))}
                        actions={(row) => [
                            { text: 'View Order', icon: <Eye className="w-4 h-4 text-primary" />, onClick: (r) => navigate(RoutesLink.ViewOrders.replace(':id', r.id)) },
                        ]}
                        emptyMessage="No failed orders"
                    />
                )}
            </div>
        </SectionCard>
    );
};
